import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { useSeasons } from '../hooks/useSeasons'
import { useFilms } from '../hooks/useFilms'
import { FilmCard } from '../components/FilmCard'

function FilmsGrid({ seasonNumber }: { seasonNumber: number }) {
  const { films, loading, error } = useFilms(seasonNumber)

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="aspect-video w-full rounded-lg" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return <p className="text-destructive text-sm">{error}</p>
  }

  if (films.length === 0) {
    return <p className="text-muted-foreground text-sm">No films published for this season yet.</p>
  }

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {films.map((film) => (
        <FilmCard key={film.id} film={film} />
      ))}
    </div>
  )
}

export function SeasonsPage() {
  const { seasons, loading } = useSeasons()

  // Default to highest-numbered season (last in ordered array)
  const defaultSeason = seasons.at(-1)

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <Skeleton className="mb-4 h-10 w-64" />
        <Skeleton className="mb-8 h-5 w-96" />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="aspect-video rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  if (seasons.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-muted-foreground">No seasons published yet.</p>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="mb-10 max-w-2xl">
        <h1 className="mb-3 text-4xl font-bold tracking-tight">One Day Dance</h1>
        <p className="text-muted-foreground leading-relaxed">
          Our flagship 24-hour dance film series. Each season, choreographers create new work under
          one thrilling constraint: 24 hours to rehearse, film, and edit. Pressure into possibility.
        </p>
      </div>

      <Tabs
        defaultValue={String(defaultSeason?.number ?? seasons[0]?.number)}
      >
        <TabsList className="mb-8 flex-wrap h-auto gap-1">
          {[...seasons].reverse().map((season) => (
            <TabsTrigger key={season.id} value={String(season.number)}>
              Season {season.number}
            </TabsTrigger>
          ))}
        </TabsList>

        {seasons.map((season) => (
          <TabsContent key={season.id} value={String(season.number)}>
            {season.description && (
              <p className="mb-8 text-muted-foreground max-w-prose">{season.description}</p>
            )}
            <FilmsGrid seasonNumber={season.number} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
